<script setup lang="ts">
	import { computed, ref, reactive } from "vue";
	import { useAuth } from "@ptx-autonomous/libs/api";
	import { getApiUrl } from "@/aws-configuration";
	import { useSettings } from "@/composables/settings";
	import EyeSlashIcon from "@/icons/EyeSlashIcon.vue";
	import EyeIcon from "@/icons/EyeIcon.vue";
	import SpinnerIcon from "@/icons/SpinnerIcon.vue";
	import WarningIcon from "@/icons/WarningIcon.vue";

	const emit = defineEmits<{
		loginSuccess: [];
	}>();

	const form = reactive({
		email: "",
		password: "",
	});

	const { apiVersion } = useSettings();
	const { signIn, forgotPassword, resetPassword } = useAuth();

	const isLoading = ref(false);
	const error = ref("");
	const showPassword = ref(false);
	const showNewPassword = ref(false);
	const needsNewPassword = ref(false);
	const completeNewPassword = ref<
		((password: string, apiUrl: string) => Promise<void>) | null
	>(null);
	const needsPasswordReset = ref(false);
	const resetStep = ref<"request" | "confirm">("request");
	const resetForm = reactive({
		code: "",
		newPassword: "",
	});
	const resetHandler = ref<
		| ((confirmationCode: string, newPassword: string) => Promise<void>)
		| null
	>(null);
	const passwordValue = computed({
		get: () =>
			needsPasswordReset.value ? resetForm.newPassword : form.password,
		set: value => {
			if (needsPasswordReset.value) {
				resetForm.newPassword = value;
			} else {
				form.password = value;
			}
		},
	});

	const handleLogin = async () => {
		if (!form.email || !form.password) {
			error.value = "Please enter both email and password";
			return;
		}

		isLoading.value = true;
		try {
			error.value = "";
			const result = await signIn(
				form.email,
				form.password,
				getApiUrl(apiVersion.value),
			);

			if (result.user) {
				emit("loginSuccess");
			} else if (result.completeNewPassword) {
				needsNewPassword.value = true;
				completeNewPassword.value = result.completeNewPassword;
				form.password = "";
				isLoading.value = false;
			}
		} catch (e) {
			console.error("Login error:", e);
			error.value = (e as Error).message;
			isLoading.value = false;
			needsNewPassword.value = false;
			completeNewPassword.value = null;
		}
	};

	const handleSetPassword = async () => {
		if (!form.password) {
			error.value = "Please enter a new password";
			return;
		}

		error.value = "";
		isLoading.value = true;
		try {
			if (!completeNewPassword.value) {
				throw new Error("Missing new password handler");
			}
			await completeNewPassword.value(
				form.password,
				getApiUrl(apiVersion.value),
			);
			isLoading.value = false;
			emit("loginSuccess");
		} catch (e) {
			console.error("Set password error:", e);
			error.value = (e as Error).message;
			isLoading.value = false;
			needsNewPassword.value = false;
			completeNewPassword.value = null;
		}
	};

	const startPasswordReset = () => {
		error.value = "";
		needsPasswordReset.value = true;
		resetStep.value = "request";
		resetForm.code = "";
		resetForm.newPassword = "";
		resetHandler.value = null;
	};

	const cancelPasswordReset = () => {
		error.value = "";
		isLoading.value = false;
		needsPasswordReset.value = false;
		resetStep.value = "request";
		resetForm.code = "";
		resetForm.newPassword = "";
		resetHandler.value = null;
	};

	const handleForgotPassword = async () => {
		if (!form.email) {
			error.value = "Please enter your email";
			return;
		}

		error.value = "";
		isLoading.value = true;
		try {
			resetHandler.value = await forgotPassword(form.email);
			resetStep.value = "confirm";
			isLoading.value = false;
		} catch (e) {
			console.error("Forgot password error:", e);
			error.value = (e as Error).message;
			isLoading.value = false;
		}
	};

	const handleResetPassword = async () => {
		if (!form.email || !resetForm.code || !resetForm.newPassword) {
			error.value = "Please enter the code and new password";
			return;
		}

		error.value = "";
		isLoading.value = true;
		try {
			if (resetHandler.value) {
				await resetHandler.value(resetForm.code, resetForm.newPassword);
			} else {
				await resetPassword(
					form.email,
					resetForm.code,
					resetForm.newPassword,
				);
			}
			isLoading.value = false;
			cancelPasswordReset();
		} catch (e) {
			console.error("Reset password error:", e);
			error.value = (e as Error).message;
			isLoading.value = false;
		}
	};

	const handleSubmit = () => {
		if (needsPasswordReset.value) {
			if (resetStep.value === "request") {
				handleForgotPassword();
			} else {
				handleResetPassword();
			}
		} else if (needsNewPassword.value) {
			handleSetPassword();
		} else {
			handleLogin();
		}
	};

	const handleKeydown = (event: KeyboardEvent) => {
		if (event.key === "Enter") {
			handleSubmit();
		}
	};
</script>

<template>
	<div
		class="login-overlay fixed inset-0 flex items-center justify-center z-50"
	>
		<div class="bg-white rounded-lg shadow-xl p-8 w-full max-w-md mx-4">
			<div class="flex flex-col justify-center items-center text-center">
				<div class="flex flex-col items-center justify-center mb-2">
					<img
						src="/ptx-logo.svg"
						alt="PTX Logo"
						class="h-16 mb-1"
					/>
					<h1 class="text-green-800 title !text-3xl">PTxAg</h1>
				</div>
				<div class="heading text-xl mb-2">Tillage Path Planner</div>
				<div
					v-if="needsNewPassword || needsPasswordReset"
					class="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4"
				>
					<div class="flex flex-col items-center">
						<div class="flex-shrink-0 flex">
							<WarningIcon class="h-5 w-5 text-yellow-400" />
							<h3
								class="text-sm font-medium text-yellow-800 ml-2"
							>
								{{
									needsPasswordReset
										? "Password Reset"
										: "Password Change Required"
								}}
							</h3>
						</div>
						<div class="mt-2">
							<p class="mt-1 text-sm text-yellow-700">
								{{
									needsPasswordReset
										? resetStep === "request"
											? "Enter your email to receive a reset code."
											: "Enter the code and your new password."
										: "Please set a new password to continue."
								}}
							</p>
						</div>
					</div>
				</div>
				<p
					v-if="!needsNewPassword && !needsPasswordReset"
					class="text-grey-600 mb-4"
				>
					Sign in to start planning your tillage operations
				</p>
			</div>

			<form
				@submit.prevent="handleSubmit"
				class="space-y-6"
			>
				<div>
					<label
						for="email"
						class="block text-sm font-medium text-grey-700 mb-2"
					>
						Email
					</label>
					<input
						id="email"
						v-model="form.email"
						type="email"
						autocomplete="email"
						required
						class="w-full px-3 py-2 border border-grey-300 rounded-md shadow-sm placeholder-grey-400 focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-green-600"
						placeholder="Enter your email"
						@keydown="handleKeydown"
						:disabled="
							isLoading ||
							needsNewPassword ||
							(needsPasswordReset && resetStep === 'confirm')
						"
					/>
				</div>

				<div v-if="!needsPasswordReset || resetStep === 'confirm'">
					<label
						for="password"
						class="block text-sm font-medium text-grey-700 mb-2"
					>
						{{
							needsNewPassword || needsPasswordReset
								? "New Password"
								: "Password"
						}}
					</label>
					<div class="relative">
						<input
							id="password"
							v-model="passwordValue"
							:type="
								needsNewPassword || needsPasswordReset
									? showNewPassword
										? 'text'
										: 'password'
									: showPassword
										? 'text'
										: 'password'
							"
							:autocomplete="
								needsNewPassword || needsPasswordReset
									? 'new-password'
									: 'current-password'
							"
							required
							class="w-full px-3 py-2 pr-10 border border-grey-300 rounded-md shadow-sm placeholder-grey-400 focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-green-600"
							:placeholder="
								needsNewPassword || needsPasswordReset
									? 'Enter your new password'
									: 'Enter your password'
							"
							@keydown="handleKeydown"
							:disabled="isLoading"
						/>
						<button
							type="button"
							class="absolute inset-y-0 right-0 flex items-center pr-3 text-grey-400 hover:text-grey-600"
							@click="
								needsNewPassword || needsPasswordReset
									? (showNewPassword = !showNewPassword)
									: (showPassword = !showPassword)
							"
							:disabled="isLoading"
						>
							<EyeSlashIcon
								v-if="
									needsNewPassword || needsPasswordReset
										? showNewPassword
										: showPassword
								"
							/>
							<EyeIcon v-else />
						</button>
					</div>
				</div>

				<div v-if="needsPasswordReset && resetStep === 'confirm'">
					<label
						for="reset-code"
						class="block text-sm font-medium text-grey-700 mb-2"
					>
						Verification Code
					</label>
					<input
						id="reset-code"
						v-model="resetForm.code"
						type="text"
						inputmode="numeric"
						autocomplete="one-time-code"
						required
						class="w-full px-3 py-2 border border-grey-300 rounded-md shadow-sm placeholder-grey-400 focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-green-600"
						placeholder="Enter the code from your email"
						@keydown="handleKeydown"
						:disabled="isLoading"
					/>
				</div>

				<div
					v-if="!needsNewPassword && !needsPasswordReset"
					class="text-right"
				>
					<button
						type="button"
						class="text-sm text-green-700 hover:text-green-900"
						@click="startPasswordReset"
					>
						Forgot password?
					</button>
				</div>
				<div
					v-else-if="needsPasswordReset"
					class="text-right"
				>
					<button
						type="button"
						class="text-sm text-green-700 hover:text-green-900"
						@click="cancelPasswordReset"
					>
						Back to sign in
					</button>
				</div>

				<div
					v-if="error"
					class="text-red-600 text-sm text-center"
				>
					{{ error }}
				</div>

				<button
					type="submit"
					class="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
					:disabled="isLoading"
				>
					<SpinnerIcon v-if="isLoading" />
					{{
						isLoading
							? needsPasswordReset
								? resetStep === "request"
									? "Sending Code..."
									: "Resetting Password..."
								: needsNewPassword
									? "Setting Password..."
									: "Signing In..."
							: needsPasswordReset
								? resetStep === "request"
									? "Send Reset Code"
									: "Reset Password"
								: needsNewPassword
									? "Set Password"
									: "Sign In"
					}}
				</button>
			</form>
		</div>
	</div>
</template>

<style scoped>
	.login-overlay {
		background-color: rgba(0, 0, 0, 0.4);
	}
</style>
